import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

/**
 * Reusable logo component.
 *
 * Two ways to identify the logo:
 *  - `vendorSlug`: looks up a UC vendor in `uc_vendor_logos`.
 *    Accepts either a canonical slug (e.g. "microsoft-teams-phone") or
 *    a display string (e.g. "Microsoft Teams Phone") — both resolve.
 *  - `partnerId`: looks up the partner's logo via `partners.logo_image_id`
 *    (falls back to `partners.logo_url` if the library row isn't ready yet).
 *
 * Alt text is taken from the image library row, defaulting to "{Name} logo".
 * When no image is found, falls back to a small text chip so nothing breaks.
 */

type ImageRow = {
  id: string;
  storage_path: string;
  alt_text: string | null;
  original_url: string | null;
};

const SIZE_CLASSES: Record<string, string> = {
  sm: "h-6",
  md: "h-8",
  lg: "h-10",
  xl: "h-12",
};

function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getPublicUrl(path: string): string {
  const { data } = supabase.storage.from("images").getPublicUrl(path);
  return data.publicUrl;
}

export function resolveUrl(image: ImageRow): string {
  if (image.storage_path.startsWith("external/") && image.original_url) {
    return image.original_url;
  }
  if (image.storage_path.startsWith("legacy/")) {
    return `/images/${image.storage_path.slice(7)}`;
  }
  return getPublicUrl(image.storage_path);
}

/** Cached lookup of all UC vendor logos (small table, fetch once). */
function useUcVendorLogos() {
  return useQuery({
    queryKey: ["uc-vendor-logos"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("uc_vendor_logos" as any)
        .select("slug, vendor_name, display_name, image_id, active")
        .eq("active", true);
      if (error) throw error;
      return (data as any[]) ?? [];
    },
  });
}

function useImage(imageId: string | null | undefined) {
  return useQuery({
    queryKey: ["image-library", imageId],
    enabled: !!imageId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ImageRow | null> => {
      const { data, error } = await supabase
        .from("image_library")
        .select("id, storage_path, alt_text, original_url")
        .eq("id", imageId!)
        .maybeSingle();
      if (error) throw error;
      return (data as ImageRow) ?? null;
    },
  });
}

function usePartnerLogo(partnerId: string | null | undefined) {
  return useQuery({
    queryKey: ["partner-logo", partnerId],
    enabled: !!partnerId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partners_public" as any)
        .select("name, logo_url, logo_image_id")
        .eq("id", partnerId!)
        .maybeSingle();
      if (error) throw error;
      return (data as any) ?? null;
    },
  });
}

export interface LogoProps {
  /** UC vendor — slug ("microsoft-teams-phone") or display string ("Microsoft Teams Phone"). */
  vendorSlug?: string;
  /** Partner id (UUID). */
  partnerId?: string;
  /** Override the displayed text fallback (defaults to vendor display name / partner name). */
  fallbackLabel?: string;
  /** Height bucket. */
  size?: "sm" | "md" | "lg" | "xl";
  /** Whether to wrap in a white pill chip (good on coloured backgrounds). */
  chip?: boolean;
  /** Extra classes for the outer wrapper. */
  className?: string;
  /** Extra classes for the inner <img> (overrides default height/width when given). */
  imgClassName?: string;
  /** Optional explicit alt text override. */
  alt?: string;
}

export function Logo({
  vendorSlug,
  partnerId,
  fallbackLabel,
  size = "md",
  chip = false,
  className,
  imgClassName,
  alt,
}: LogoProps) {
  const isVendor = !!vendorSlug;
  const lookupSlug = vendorSlug ? slugify(vendorSlug) : null;

  const { data: vendors } = useUcVendorLogos();
  const vendorRow = isVendor && lookupSlug
    ? vendors?.find(
        (v: any) =>
          v.slug === lookupSlug ||
          (v.display_name && slugify(v.display_name) === lookupSlug) ||
          (v.vendor_name && slugify(v.vendor_name) === lookupSlug),
      ) ?? null
    : null;

  const { data: vendorImage } = useImage(vendorRow?.image_id);
  const { data: partner } = usePartnerLogo(partnerId);
  const { data: partnerImage } = useImage(partner?.logo_image_id);

  // Resolve src + alt + label
  let src: string | null = null;
  let resolvedAlt: string | null = null;
  let label: string =
    fallbackLabel ||
    (isVendor ? vendorRow?.display_name ?? vendorSlug ?? "" : partner?.name ?? "");

  if (isVendor && vendorImage) {
    src = resolveUrl(vendorImage);
    resolvedAlt = vendorImage.alt_text || `${vendorRow?.display_name ?? label} logo`;
    label = vendorRow?.display_name ?? label;
  } else if (partnerId) {
    if (partnerImage) {
      src = resolveUrl(partnerImage);
      resolvedAlt = partnerImage.alt_text || `${partner?.name ?? label} logo`;
    } else if (partner?.logo_url) {
      src = partner.logo_url;
      resolvedAlt = `${partner.name} logo`;
    }
    label = partner?.name ?? label;
  }

  const heightClass = SIZE_CLASSES[size] ?? SIZE_CLASSES.md;
  const chipWrapper = chip
    ? "inline-flex items-center justify-center bg-background border border-border rounded-md px-2.5 py-1.5 shadow-sm"
    : "inline-flex items-center";

  // Image available — render it
  if (src) {
    return (
      <span className={cn(chipWrapper, className)}>
        <img
          src={src}
          alt={alt ?? resolvedAlt ?? `${label} logo`}
          loading="lazy"
          decoding="async"
          className={cn(heightClass, "w-auto object-contain", imgClassName)}
        />
      </span>
    );
  }

  // No image yet — text fallback that still reads as a chip
  return (
    <span
      className={cn(
        "inline-flex items-center bg-background border border-border rounded-md px-2.5 py-1 text-xs font-medium text-foreground",
        className,
      )}
    >
      {label || "Logo"}
    </span>
  );
}

/** Helper exposed so a parent can also slugify a platform string the same way. */
export const ucVendorSlug = slugify;